import { callTheme, videoPill, CALL_TYPE, type CallTheme } from "./callTheme";

/**
 * The connecting / reconnecting veil (VC3-9).
 *
 * Shown in three places that each used to draw their own: while the lazy media
 * surface loads (IncomingCallOverlay and CareScheme's OutboundCall), and inside
 * CallMediaSurface until the first remote frame arrives or while LiveKit is
 * reconnecting. Three copies meant three different backgrounds, sizes and
 * weights for the same half-second of the same call.
 *
 * It stays near-black rather than adopting the care scheme's warm paper, and
 * that is deliberate: this veil is the pre-roll of a full-bleed video plane, and
 * flashing a bright card up a moment before live video would be worse than the
 * off-theme it fixes. What it borrows from the scheme is the typeface, the
 * accent green and the pill geometry.
 *
 * The pulsing dot is not decoration. A still line of text on a black screen
 * gives a resident no way to tell "working on it" from "frozen" - and a frozen
 * call is a thing this product has actually shipped (see the media-plane and
 * permission-wait notes). It respects `prefers-reduced-motion`.
 */
export default function CallVeil({
  label,
  note,
  theme,
}: {
  /** The one line that says what is happening, e.g. "Ansluter till Anna…". */
  label: string;
  /** Optional reassurance underneath - why waiting is normal. */
  note?: string;
  theme?: CallTheme;
}) {
  const t = theme ?? callTheme();
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "absolute",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background: t.video,
        color: t.onVideo,
        fontFamily: t.sans,
        padding: 80,
        textAlign: "center",
      }}
    >
      <div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 22,
            ...videoPill(t),
            background: "rgba(255,255,255,0.06)",
            fontSize: CALL_TYPE.chrome + 10,
            fontWeight: 600,
            padding: "22px 44px",
          }}
        >
          <span
            aria-hidden="true"
            className="foyer-call-veil-dot"
            style={{
              width: 22,
              height: 22,
              borderRadius: 999,
              background: t.greenSoft,
              flex: "0 0 auto",
              animation: "foyerCallVeilPulse 1.6s ease-in-out infinite",
            }}
          />
          <span>{label}</span>
        </div>
        {note && (
          <div
            style={{
              marginTop: 28,
              fontSize: CALL_TYPE.hint,
              color: "rgba(255,255,255,0.62)",
              lineHeight: 1.4,
            }}
          >
            {note}
          </div>
        )}
      </div>
      <style>{`
        @keyframes foyerCallVeilPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.25 } }
        @media (prefers-reduced-motion: reduce) {
          .foyer-call-veil-dot { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
